"use client";

import { c as _c } from "react/compiler-runtime";
import { cva, type VariantProps } from "class-variance-authority";
import { PanelLeft } from "lucide-react";
import { Slot as SlotPrimitive } from "radix-ui";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
const SIDEBAR_COOKIE_NAME = "sidebar_state";
const SIDEBAR_WIDTH_COOKIE_NAME = "sidebar_width";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "14rem";
const SIDEBAR_WIDTH_DEFAULT_PX = 224; // 14rem in pixels
const SIDEBAR_WIDTH_MIN_PX = 200;
const SIDEBAR_WIDTH_MAX_VW = 40; // percentage of viewport width
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT = ".";
type SidebarContextProps = {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
  sidebarWidth: number;
  isDragging: boolean;
  startResize: (e: React.MouseEvent, side: "left" | "right") => void;
  resetWidth: () => void;
};
const SidebarContext = React.createContext<SidebarContextProps | null>(null);
function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }
  return context;
}
const SidebarProvider = React.forwardRef<HTMLDivElement, React.ComponentProps<"div"> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When true, the sidebar uses relative positioning and fits within its container */
  contained?: boolean;
}>((t0, ref) => {
  const $ = _c(51);
  let children;
  let className;
  let openProp;
  let props;
  let setOpenProp;
  let style;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      defaultOpen: t1,
      open: openProp,
      onOpenChange: setOpenProp,
      contained: t2,
      className,
      style,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = openProp;
    $[4] = props;
    $[5] = setOpenProp;
    $[6] = style;
    $[7] = t1;
    $[8] = t2;
  } else {
    children = $[1];
    className = $[2];
    openProp = $[3];
    props = $[4];
    setOpenProp = $[5];
    style = $[6];
    t1 = $[7];
    t2 = $[8];
  }
  const defaultOpen = t1 === undefined ? true : t1;
  const contained = t2 === undefined ? false : t2;
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [sidebarWidth, setSidebarWidth] = React.useState(_temp);
  const persistWidth = _temp2;
  const dragState = React.useRef(null);
  let t3;
  if ($[9] !== sidebarWidth) {
    t3 = (e, side) => {
      e.preventDefault();
      dragState.current = {
        startX: e.clientX,
        startWidth: sidebarWidth,
        side,
        moved: false
      };
      setIsDragging(true);
    };
    $[9] = sidebarWidth;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleDragStart = t3;
  let t4;
  let t5;
  if ($[11] !== isDragging) {
    t4 = () => {
      if (!isDragging) {
        return;
      }
      const maxPx = window.innerWidth * SIDEBAR_WIDTH_MAX_VW / 100;
      const onMouseMove = e_0 => {
        if (!dragState.current) {
          return;
        }
        const {
          startX,
          startWidth,
          side: side_0
        } = dragState.current;
        const delta = side_0 === "left" ? e_0.clientX - startX : startX - e_0.clientX;
        if (Math.abs(delta) > 2) {
          dragState.current.moved = true;
        }
        const newWidth = Math.round(Math.min(maxPx, Math.max(SIDEBAR_WIDTH_MIN_PX, startWidth + delta)));
        setSidebarWidth(newWidth);
      };
      const onMouseUp = () => {
        setIsDragging(false);
        if (dragState.current) {
          if (!dragState.current.moved) {}
          dragState.current = null;
        }
        setSidebarWidth(w => {
          persistWidth(w);
          return w;
        });
      };
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      return () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    };
    t5 = [isDragging, persistWidth];
    $[11] = isDragging;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  React.useEffect(t4, t5);
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setSidebarWidth(SIDEBAR_WIDTH_DEFAULT_PX);
      persistWidth(SIDEBAR_WIDTH_DEFAULT_PX);
    };
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const resetWidth = t6;
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  let t7;
  if ($[15] !== open || $[16] !== setOpenProp) {
    t7 = value => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    };
    $[15] = open;
    $[16] = setOpenProp;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const setOpen = t7;
  let t8;
  if ($[18] !== isMobile || $[19] !== setOpen) {
    t8 = () => isMobile ? setOpenMobile(_temp3) : setOpen(_temp4);
    $[18] = isMobile;
    $[19] = setOpen;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const toggleSidebar = t8;
  let t10;
  let t9;
  if ($[21] !== isMobile || $[22] !== toggleSidebar) {
    t9 = () => {
      const handleKeyDown = event => {
        if (isMobile) {
          return;
        }
        if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          toggleSidebar();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t10 = [toggleSidebar, isMobile];
    $[21] = isMobile;
    $[22] = toggleSidebar;
    $[23] = t10;
    $[24] = t9;
  } else {
    t10 = $[23];
    t9 = $[24];
  }
  React.useEffect(t9, t10);
  const state = open ? "expanded" : "collapsed";
  let t11;
  if ($[25] !== handleDragStart || $[26] !== isDragging || $[27] !== isMobile || $[28] !== open || $[29] !== openMobile || $[30] !== setOpen || $[31] !== sidebarWidth || $[32] !== state || $[33] !== toggleSidebar) {
    t11 = {
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
      sidebarWidth,
      isDragging,
      startResize: handleDragStart,
      resetWidth
    };
    $[25] = handleDragStart;
    $[26] = isDragging;
    $[27] = isMobile;
    $[28] = open;
    $[29] = openMobile;
    $[30] = setOpen;
    $[31] = sidebarWidth;
    $[32] = state;
    $[33] = toggleSidebar;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const contextValue = t11;
  const t12 = contained ? "h-full min-h-0 overflow-hidden" : "min-h-svh";
  let t13;
  if ($[35] !== className || $[36] !== t12) {
    t13 = cn("group/sidebar-wrapper flex w-full has-[[data-variant=inset]]:bg-sidebar", t12, className);
    $[35] = className;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const t14 = contained ? "true" : undefined;
  const t15 = `${sidebarWidth}px`;
  let t16;
  if ($[38] !== style || $[39] !== t15) {
    t16 = {
      "--sidebar-width": t15,
      "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
      ...style
    };
    $[38] = style;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  const t17 = t16 as React.CSSProperties;
  let t18;
  if ($[41] !== children || $[42] !== props || $[43] !== ref || $[44] !== t13 || $[45] !== t14 || $[46] !== t17) {
    t18 = <TooltipProvider delayDuration={0}><div className={t13} data-contained={t14} ref={ref} style={t17} {...props}>{children}</div></TooltipProvider>;
    $[41] = children;
    $[42] = props;
    $[43] = ref;
    $[44] = t13;
    $[45] = t14;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== contextValue || $[49] !== t18) {
    t19 = <SidebarContext.Provider value={contextValue}>{t18}</SidebarContext.Provider>;
    $[48] = contextValue;
    $[49] = t18;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  return t19;
});
SidebarProvider.displayName = "SidebarProvider";
const Sidebar = React.forwardRef<HTMLDivElement, React.ComponentProps<"div"> & {
  side?: "left" | "right";
  variant?: "sidebar" | "floating" | "inset";
  collapsible?: "offcanvas" | "icon" | "none";
  /** When true, uses relative positioning instead of fixed (for embedded use) */
  contained?: boolean;
}>((t0, ref) => {
  const $ = _c(72);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      side: t1,
      variant: t2,
      collapsible: t3,
      contained: t4,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
  }
  const side = t1 === undefined ? "left" : t1;
  const variant = t2 === undefined ? "sidebar" : t2;
  const collapsible = t3 === undefined ? "offcanvas" : t3;
  const contained = t4 === undefined ? false : t4;
  const {
    isMobile,
    state,
    openMobile,
    setOpenMobile,
    isDragging
  } = useSidebar();
  if (collapsible === "none") {
    let t5;
    if ($[8] !== className) {
      t5 = cn("flex h-full w-[var(--sidebar-width)] flex-col bg-sidebar text-sidebar-foreground", className);
      $[8] = className;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== children || $[11] !== props || $[12] !== ref || $[13] !== t5) {
      t6 = <div className={t5} ref={ref} {...props}>{children}</div>;
      $[10] = children;
      $[11] = props;
      $[12] = ref;
      $[13] = t5;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    return t6;
  }
  if (isMobile) {
    const t5 = contained ? "top-0 h-full" : "top-14 h-[calc(100%-3.5rem)]";
    let t6;
    if ($[15] !== t5) {
      t6 = cn("w-[var(--sidebar-width)] bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden", t5);
      $[15] = t5;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    const t7 = contained ? "top-0" : "top-14";
    let t8;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        "--sidebar-width": SIDEBAR_WIDTH_MOBILE
      };
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    let t9;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <SheetHeader className="sr-only"><SheetTitle>Sidebar</SheetTitle><SheetDescription>Displays the mobile sidebar.</SheetDescription></SheetHeader>;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[19] !== children) {
      t10 = <div className="flex h-full w-full flex-col">{children}</div>;
      $[19] = children;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    let t11;
    if ($[21] !== side || $[22] !== t10 || $[23] !== t6 || $[24] !== t7) {
      t11 = <SheetContent className={t6} overlayClassName={t7} data-mobile="true" data-sidebar="sidebar" side={side} style={t8 as React.CSSProperties}>{t9}{t10}</SheetContent>;
      $[21] = side;
      $[22] = t10;
      $[23] = t6;
      $[24] = t7;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    let t12;
    if ($[26] !== openMobile || $[27] !== props || $[28] !== setOpenMobile || $[29] !== t11) {
      t12 = <Sheet onOpenChange={setOpenMobile} open={openMobile} {...props}>{t11}</Sheet>;
      $[26] = openMobile;
      $[27] = props;
      $[28] = setOpenMobile;
      $[29] = t11;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    return t12;
  }
  if (contained) {
    const t5 = state === "collapsed" ? collapsible : "";
    const t6 = !isDragging && "transition-[width] duration-200 ease-linear";
    const t7 = variant === "floating" || variant === "inset" ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]" : "group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)] group-data-[side=left]:border-r group-data-[side=right]:border-l";
    let t8;
    if ($[31] !== className || $[32] !== t6 || $[33] !== t7) {
      t8 = cn("h-full w-[var(--sidebar-width)] md:flex flex-col", t6, "group-data-[collapsible=offcanvas]:w-0 group-data-[collapsible=offcanvas]:overflow-hidden", t7, className);
      $[31] = className;
      $[32] = t6;
      $[33] = t7;
      $[34] = t8;
    } else {
      t8 = $[34];
    }
    let t9;
    if ($[35] !== children) {
      t9 = <div className="relative flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow" data-sidebar="sidebar">{children}</div>;
      $[35] = children;
      $[36] = t9;
    } else {
      t9 = $[36];
    }
    let t10;
    if ($[37] !== props || $[38] !== t8 || $[39] !== t9) {
      t10 = <div className={t8} {...props}>{t9}</div>;
      $[37] = props;
      $[38] = t8;
      $[39] = t9;
      $[40] = t10;
    } else {
      t10 = $[40];
    }
    let t11;
    if ($[41] !== ref || $[42] !== side || $[43] !== state || $[44] !== t10 || $[45] !== t5 || $[46] !== variant) {
      t11 = <div className="group peer hidden text-sidebar-foreground md:block h-full relative" data-collapsible={t5} data-side={side} data-state={state} data-variant={variant} data-contained="true" ref={ref}>{t10}</div>;
      $[41] = ref;
      $[42] = side;
      $[43] = state;
      $[44] = t10;
      $[45] = t5;
      $[46] = variant;
      $[47] = t11;
    } else {
      t11 = $[47];
    }
    return t11;
  }
  const t5 = state === "collapsed" ? collapsible : "";
  const t6 = !isDragging && "transition-[width] duration-200 ease-linear";
  const t7 = variant === "floating" || variant === "inset" ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]" : "group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)]";
  let t8;
  if ($[48] !== t6 || $[49] !== t7) {
    t8 = cn("relative w-[var(--sidebar-width)] bg-transparent", t6, "group-data-[collapsible=offcanvas]:w-0", "group-data-[side=right]:rotate-180", t7);
    $[48] = t6;
    $[49] = t7;
    $[50] = t8;
  } else {
    t8 = $[50];
  }
  let t9;
  if ($[51] !== t8) {
    t9 = <div className={t8} />;
    $[51] = t8;
    $[52] = t9;
  } else {
    t9 = $[52];
  }
  const t10 = !isDragging && "transition-[left,right,width] duration-200 ease-linear";
  const t11 = side === "left" ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]" : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]";
  const t12 = variant === "floating" || variant === "inset" ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)]" : "group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)] group-data-[side=left]:border-r group-data-[side=right]:border-l";
  let t13;
  if ($[53] !== className || $[54] !== t10 || $[55] !== t11 || $[56] !== t12) {
    t13 = cn("fixed inset-y-0 z-10 hidden h-svh w-[var(--sidebar-width)] md:flex", t10, t11, t12, className);
    $[53] = className;
    $[54] = t10;
    $[55] = t11;
    $[56] = t12;
    $[57] = t13;
  } else {
    t13 = $[57];
  }
  let t14;
  if ($[58] !== children) {
    t14 = <div className="flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow" data-sidebar="sidebar">{children}</div>;
    $[58] = children;
    $[59] = t14;
  } else {
    t14 = $[59];
  }
  let t15;
  if ($[60] !== props || $[61] !== t13 || $[62] !== t14) {
    t15 = <div className={t13} {...props}>{t14}</div>;
    $[60] = props;
    $[61] = t13;
    $[62] = t14;
    $[63] = t15;
  } else {
    t15 = $[63];
  }
  let t16;
  if ($[64] !== ref || $[65] !== side || $[66] !== state || $[67] !== t15 || $[68] !== t5 || $[69] !== t9 || $[70] !== variant) {
    t16 = <div className="group peer hidden text-sidebar-foreground md:block" data-collapsible={t5} data-side={side} data-state={state} data-variant={variant} ref={ref}>{t9}{t15}</div>;
    $[64] = ref;
    $[65] = side;
    $[66] = state;
    $[67] = t15;
    $[68] = t5;
    $[69] = t9;
    $[70] = variant;
    $[71] = t16;
  } else {
    t16 = $[71];
  }
  return t16;
});
Sidebar.displayName = "Sidebar";
const SidebarTrigger = React.forwardRef<React.ElementRef<typeof Button>, React.ComponentProps<typeof Button>>((t0, ref) => {
  const $ = _c(16);
  let className;
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      onClick,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onClick;
    $[3] = props;
  } else {
    className = $[1];
    onClick = $[2];
    props = $[3];
  }
  const {
    toggleSidebar
  } = useSidebar();
  let t1;
  if ($[4] !== className) {
    t1 = cn("h-7 w-7", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== onClick || $[7] !== toggleSidebar) {
    t2 = event => {
      onClick?.(event);
      toggleSidebar();
    };
    $[6] = onClick;
    $[7] = toggleSidebar;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <PanelLeft />;
    t4 = <span className="sr-only">Toggle Sidebar</span>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  let t5;
  if ($[11] !== props || $[12] !== ref || $[13] !== t1 || $[14] !== t2) {
    t5 = <Button className={t1} data-sidebar="trigger" onClick={t2} ref={ref} size="icon" variant="ghost" {...props}>{t3}{t4}</Button>;
    $[11] = props;
    $[12] = ref;
    $[13] = t1;
    $[14] = t2;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
});
SidebarTrigger.displayName = "SidebarTrigger";
const SidebarRail = React.forwardRef<HTMLButtonElement, React.ComponentProps<"button"> & {
  side?: "left" | "right";
  contained?: boolean;
}>((t0, ref) => {
  const $ = _c(20);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      side: t1,
      contained: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const side = t1 === undefined ? "left" : t1;
  const contained = t2 === undefined ? false : t2;
  const {
    startResize,
    resetWidth
  } = useSidebar();
  const t3 = contained ? "right-0 w-1 hover:bg-sidebar-border transition-colors" : "-translate-x-1/2 group-data-[side=left]:-right-4 w-4 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-sidebar-border";
  const t4 = !contained && "group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:hover:bg-sidebar group-data-[collapsible=offcanvas]:after:left-full";
  const t5 = !contained && "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2";
  const t6 = !contained && "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2";
  let t7;
  if ($[5] !== className || $[6] !== t3 || $[7] !== t4 || $[8] !== t5 || $[9] !== t6) {
    t7 = cn("absolute inset-y-0 z-20 hidden cursor-col-resize sm:flex", t3, t4, t5, t6, className);
    $[5] = className;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== side || $[12] !== startResize) {
    t8 = e => startResize(e, side);
    $[11] = side;
    $[12] = startResize;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== props || $[15] !== ref || $[16] !== resetWidth || $[17] !== t7 || $[18] !== t8) {
    t9 = <button aria-label="Resize Sidebar" className={t7} data-sidebar="rail" onMouseDown={t8} onDoubleClick={resetWidth} ref={ref} tabIndex={-1} title="Drag to resize sidebar" {...props} />;
    $[14] = props;
    $[15] = ref;
    $[16] = resetWidth;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
});
SidebarRail.displayName = "SidebarRail";
const SidebarInset = React.forwardRef<HTMLDivElement, React.ComponentProps<"main"> & {
  /** When true, uses h-full instead of flex-1 for contained layouts */
  contained?: boolean;
}>((t0, ref) => {
  const $ = _c(11);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      contained: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const contained = t1 === undefined ? false : t1;
  const t2 = contained ? "h-full min-h-0 overflow-hidden" : "flex-1";
  let t3;
  if ($[4] !== className || $[5] !== t2) {
    t3 = cn("relative flex w-full flex-col bg-background", t2, "md:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2 md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow", className);
    $[4] = className;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props || $[8] !== ref || $[9] !== t3) {
    t4 = <main className={t3} ref={ref} {...props} />;
    $[7] = props;
    $[8] = ref;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
});
SidebarInset.displayName = "SidebarInset";
const SidebarInput = React.forwardRef<React.ElementRef<typeof Input>, React.ComponentProps<typeof Input>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("h-8 w-full bg-background shadow-none focus-visible:ring-2 focus-visible:ring-sidebar-ring", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <Input className={t1} data-sidebar="input" ref={ref} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarInput.displayName = "SidebarInput";
const SidebarHeader = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-col gap-2 p-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <div className={t1} data-sidebar="header" ref={ref} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarHeader.displayName = "SidebarHeader";
const SidebarFooter = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex flex-col gap-2 p-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <div className={t1} data-sidebar="footer" ref={ref} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarFooter.displayName = "SidebarFooter";
const SidebarSeparator = React.forwardRef<React.ElementRef<typeof Separator>, React.ComponentProps<typeof Separator>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("mx-2 w-auto bg-sidebar-border", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <Separator className={t1} data-sidebar="separator" ref={ref} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarSeparator.displayName = "SidebarSeparator";
const SidebarContent = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <div className={t1} data-sidebar="content" ref={ref} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarContent.displayName = "SidebarContent";
const SidebarGroup = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("relative flex w-full min-w-0 flex-col p-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <div className={t1} data-sidebar="group" ref={ref} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarGroup.displayName = "SidebarGroup";
const SidebarGroupLabel = React.forwardRef<HTMLDivElement, React.ComponentProps<"div"> & {
  asChild?: boolean;
}>((t0, ref) => {
  const $ = _c(11);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      asChild: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const asChild = t1 === undefined ? false : t1;
  const Comp = asChild ? SlotPrimitive.Slot : "div";
  let t2;
  if ($[4] !== className) {
    t2 = cn("flex h-8 shrink-0 items-center rounded-md px-2 font-medium text-sidebar-foreground/70 text-xs outline-none ring-sidebar-ring transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0", "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== Comp || $[7] !== props || $[8] !== ref || $[9] !== t2) {
    t3 = <Comp className={t2} data-sidebar="group-label" ref={ref} {...props} />;
    $[6] = Comp;
    $[7] = props;
    $[8] = ref;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
});
SidebarGroupLabel.displayName = "SidebarGroupLabel";
const SidebarGroupAction = React.forwardRef<HTMLButtonElement, React.ComponentProps<"button"> & {
  asChild?: boolean;
}>((t0, ref) => {
  const $ = _c(11);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      asChild: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const asChild = t1 === undefined ? false : t1;
  const Comp = asChild ? SlotPrimitive.Slot : "button";
  let t2;
  if ($[4] !== className) {
    t2 = cn("absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0", "after:-inset-2 after:absolute after:md:hidden", "group-data-[collapsible=icon]:hidden", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== Comp || $[7] !== props || $[8] !== ref || $[9] !== t2) {
    t3 = <Comp className={t2} data-sidebar="group-action" ref={ref} {...props} />;
    $[6] = Comp;
    $[7] = props;
    $[8] = ref;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
});
SidebarGroupAction.displayName = "SidebarGroupAction";
const SidebarGroupContent = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("w-full text-sm", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <div className={t1} data-sidebar="group-content" ref={ref} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarGroupContent.displayName = "SidebarGroupContent";
const SidebarMenu = React.forwardRef<HTMLUListElement, React.ComponentProps<"ul">>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex w-full min-w-0 flex-col gap-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <ul className={t1} data-sidebar="menu" ref={ref} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarMenu.displayName = "SidebarMenu";
const SidebarMenuItem = React.forwardRef<HTMLLIElement, React.ComponentProps<"li">>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("group/menu-item relative", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <li className={t1} data-sidebar="menu-item" ref={ref} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarMenuItem.displayName = "SidebarMenuItem";
const sidebarMenuButtonVariants = cva("peer/menu-button group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0", {
  variants: {
    variant: {
      default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
      outline: "bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]"
    },
    size: {
      default: "h-8 text-sm",
      sm: "h-7 text-xs",
      lg: "group-data-[collapsible=icon]:!p-0 h-12 text-sm"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
const SidebarMenuButton = React.forwardRef<HTMLButtonElement, React.ComponentProps<"button"> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & VariantProps<typeof sidebarMenuButtonVariants>>((t0, ref) => {
  const $ = _c(29);
  let className;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let tooltip;
  if ($[0] !== t0) {
    ({
      asChild: t1,
      isActive: t2,
      variant: t3,
      size: t4,
      tooltip,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = tooltip;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    tooltip = $[7];
  }
  const asChild = t1 === undefined ? false : t1;
  const isActive = t2 === undefined ? false : t2;
  const variant = t3 === undefined ? "default" : t3;
  const size = t4 === undefined ? "default" : t4;
  const Comp = asChild ? SlotPrimitive.Slot : "button";
  const {
    isMobile,
    state
  } = useSidebar();
  let t5;
  if ($[8] !== className || $[9] !== size || $[10] !== variant) {
    t5 = cn(sidebarMenuButtonVariants({
      variant,
      size
    }), className);
    $[8] = className;
    $[9] = size;
    $[10] = variant;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== Comp || $[13] !== isActive || $[14] !== props || $[15] !== ref || $[16] !== size || $[17] !== t5) {
    t6 = <Comp className={t5} data-active={isActive} data-sidebar="menu-button" data-size={size} ref={ref} {...props} />;
    $[12] = Comp;
    $[13] = isActive;
    $[14] = props;
    $[15] = ref;
    $[16] = size;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const button = t6;
  if (!tooltip) {
    return button;
  }
  if (typeof tooltip === "string") {
    let t7;
    if ($[19] !== tooltip) {
      t7 = {
        children: tooltip
      };
      $[19] = tooltip;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    tooltip = t7;
  }
  let t7;
  if ($[21] !== button) {
    t7 = <TooltipTrigger asChild={true}>{button}</TooltipTrigger>;
    $[21] = button;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const t8 = state !== "collapsed" || isMobile;
  let t9;
  if ($[23] !== t8 || $[24] !== tooltip) {
    t9 = <TooltipContent align="center" hidden={t8} side="right" {...tooltip} />;
    $[23] = t8;
    $[24] = tooltip;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t7 || $[27] !== t9) {
    t10 = <Tooltip>{t7}{t9}</Tooltip>;
    $[26] = t7;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
});
SidebarMenuButton.displayName = "SidebarMenuButton";
const SidebarMenuAction = React.forwardRef<HTMLButtonElement, React.ComponentProps<"button"> & {
  asChild?: boolean;
  showOnHover?: boolean;
}>((t0, ref) => {
  const $ = _c(13);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      asChild: t1,
      showOnHover: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const asChild = t1 === undefined ? false : t1;
  const showOnHover = t2 === undefined ? false : t2;
  const Comp = asChild ? SlotPrimitive.Slot : "button";
  const t3 = showOnHover && "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground md:opacity-0";
  let t4;
  if ($[5] !== className || $[6] !== t3) {
    t4 = cn("absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 peer-hover/menu-button:text-sidebar-accent-foreground [&>svg]:size-4 [&>svg]:shrink-0", "after:-inset-2 after:absolute after:md:hidden", "peer-data-[size=sm]/menu-button:top-1", "peer-data-[size=default]/menu-button:top-1.5", "peer-data-[size=lg]/menu-button:top-2.5", "group-data-[collapsible=icon]:hidden", t3, className);
    $[5] = className;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== Comp || $[9] !== props || $[10] !== ref || $[11] !== t4) {
    t5 = <Comp className={t4} data-sidebar="menu-action" ref={ref} {...props} />;
    $[8] = Comp;
    $[9] = props;
    $[10] = ref;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
});
SidebarMenuAction.displayName = "SidebarMenuAction";
const SidebarMenuBadge = React.forwardRef<HTMLDivElement, React.ComponentProps<"div">>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("pointer-events-none absolute right-1 flex h-5 min-w-5 select-none items-center justify-center rounded-md px-1 font-medium text-sidebar-foreground text-xs tabular-nums", "peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground", "peer-data-[size=sm]/menu-button:top-1", "peer-data-[size=default]/menu-button:top-1.5", "peer-data-[size=lg]/menu-button:top-2.5", "group-data-[collapsible=icon]:hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <div className={t1} data-sidebar="menu-badge" ref={ref} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarMenuBadge.displayName = "SidebarMenuBadge";
const SidebarMenuSkeleton = React.forwardRef<HTMLDivElement, React.ComponentProps<"div"> & {
  showIcon?: boolean;
}>((t0, ref) => {
  const $ = _c(15);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      showIcon: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const showIcon = t1 === undefined ? false : t1;
  const width = `${Math.floor(Math.random() * 40) + 50}%`;
  let t2;
  if ($[4] !== className) {
    t2 = cn("flex h-8 items-center gap-2 rounded-md px-2", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== showIcon) {
    t3 = showIcon && <Skeleton className="size-4 rounded-md" data-sidebar="menu-skeleton-icon" />;
    $[6] = showIcon;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      "--skeleton-width": width
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Skeleton className="h-4 max-w-[var(--skeleton-width)] flex-1" data-sidebar="menu-skeleton-text" style={t4 as React.CSSProperties} />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== props || $[11] !== ref || $[12] !== t2 || $[13] !== t3) {
    t6 = <div className={t2} data-sidebar="menu-skeleton" ref={ref} {...props}>{t3}{t5}</div>;
    $[10] = props;
    $[11] = ref;
    $[12] = t2;
    $[13] = t3;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
});
SidebarMenuSkeleton.displayName = "SidebarMenuSkeleton";
const SidebarMenuSub = React.forwardRef<HTMLUListElement, React.ComponentProps<"ul">>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-sidebar-border border-l px-2.5 py-0.5", "group-data-[collapsible=icon]:hidden", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <ul className={t1} data-sidebar="menu-sub" ref={ref} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SidebarMenuSub.displayName = "SidebarMenuSub";
const SidebarMenuSubItem = React.forwardRef<HTMLLIElement, React.ComponentProps<"li">>((t0, ref) => {
  const $ = _c(5);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props || $[3] !== ref) {
    t1 = <li ref={ref} {...props} />;
    $[2] = props;
    $[3] = ref;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
});
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";
const SidebarMenuSubButton = React.forwardRef<HTMLAnchorElement, React.ComponentProps<"a"> & {
  asChild?: boolean;
  size?: "sm" | "md";
  isActive?: boolean;
}>((t0, ref) => {
  const $ = _c(17);
  let className;
  let isActive;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      asChild: t1,
      size: t2,
      isActive,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = isActive;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    className = $[1];
    isActive = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const asChild = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "md" : t2;
  const Comp = asChild ? SlotPrimitive.Slot : "a";
  const t3 = size === "sm" && "text-xs";
  const t4 = size === "md" && "text-sm";
  let t5;
  if ($[6] !== className || $[7] !== t3 || $[8] !== t4) {
    t5 = cn("-translate-x-px flex h-7 min-w-0 items-center gap-2 overflow-hidden rounded-md px-2 text-sidebar-foreground outline-none ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground", "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground", t3, t4, "group-data-[collapsible=icon]:hidden", className);
    $[6] = className;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== Comp || $[11] !== isActive || $[12] !== props || $[13] !== ref || $[14] !== size || $[15] !== t5) {
    t6 = <Comp className={t5} data-active={isActive} data-sidebar="menu-sub-button" data-size={size} ref={ref} {...props} />;
    $[10] = Comp;
    $[11] = isActive;
    $[12] = props;
    $[13] = ref;
    $[14] = size;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
});
SidebarMenuSubButton.displayName = "SidebarMenuSubButton";
export { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupAction, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarInput, SidebarInset, SidebarMenu, SidebarMenuAction, SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem, SidebarMenuSkeleton, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, SidebarProvider, SidebarRail, SidebarSeparator, SidebarTrigger, useSidebar };
function _temp() {
  if (typeof document === "undefined") {
    return SIDEBAR_WIDTH_DEFAULT_PX;
  }
  const match = document.cookie.match(new RegExp(`(?:^|; )${SIDEBAR_WIDTH_COOKIE_NAME}=([^;]*)`));
  const parsed = match ? parseInt(match[1], 10) : NaN;
  return Number.isFinite(parsed) ? parsed : SIDEBAR_WIDTH_DEFAULT_PX;
}
function _temp2(width) {
  document.cookie = `${SIDEBAR_WIDTH_COOKIE_NAME}=${width}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
}
function _temp3(open_0) {
  return !open_0;
}
function _temp4(open_1) {
  return !open_1;
}